import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, TrendingUp, Building2, Landmark, GraduationCap, Car, HomeIcon,
  Wallet, Lock, Award, Clock, Zap, CheckCircle2, Sparkles,
  ArrowRight, Star, Phone, Mail, MapPin, Send, KeyRound, FileCheck2, Fingerprint,
  Stethoscope, Wrench, HeartPulse,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import LeadForm from "@/components/LeadForm";
import Partners from "@/components/Partners";
import LoanCalculator from "@/components/LoanCalculator";
import HowItWorks from "@/components/HowItWorks";

const loanProducts = [
  { slug: "personal", icon: Wallet, title: "Personal Loans", desc: "Unsecured loans for life's plans.", accent: "#1B63F0", range: "$1K – $50K · 8.99–29.99% APR" },
  { slug: "debt-consolidation", icon: TrendingUp, title: "Debt Consolidation", desc: "One payment. Less stress.", accent: "#17A673", range: "$2.5K – $50K · 7.99–24.99% APR" },
  { slug: "emergency", icon: Zap, title: "Emergency Loans", desc: "Fast funding when it matters.", accent: "#D4AF37", range: "$1K – $25K · 9.99–35.99% APR" },
  { slug: "medical", icon: Stethoscope, title: "Medical Loans", desc: "Care on your schedule.", accent: "#1B63F0", range: "$1K – $50K · 7.99–27.99% APR" },
  { slug: "home-improvement", icon: Wrench, title: "Home Improvement", desc: "Finance the project.", accent: "#17A673", range: "$2.5K – $100K · 6.99–22.99% APR" },
  { slug: "auto", icon: Car, title: "Auto Loans", desc: "New, used, or refinance.", accent: "#D4AF37", range: "$5K – $100K · 5.49–18.99% APR" },
  { slug: "business", icon: Building2, title: "Business Loans", desc: "Capital that scales with you.", accent: "#1B63F0", range: "$10K – $500K · 7.99–29.99% APR" },
];

const whyChoose = [
  { icon: Zap, title: "Fast Response", desc: "Most information requests are answered within one business day." },
  { icon: Sparkles, title: "Transparent Rates", desc: "No teaser rates and no gotchas — all published up front by partners." },
  { icon: Lock, title: "Secure Communication", desc: "Modern transport encryption on every session and message." },
  { icon: ShieldCheck, title: "Transparent", desc: "Clear terms and rates disclosed up front — never buried in fine print." },
  { icon: Clock, title: "Real Support", desc: "US-based specialists reachable by phone and email during business hours." },
  { icon: Award, title: "No Hidden Fees", desc: "Any partner referral fees are always disclosed to you first." },
];

const testimonials = [
  { name: "Marcus Reeves", role: "Founder, Reeves & Co.", quote: "The team followed up the same day I filled out the form. Clear next steps, no pressure — refreshingly human.", pic: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&q=80" },
  { name: "Priya Mehta", role: "Physician, Brooklyn", quote: "I got answers to questions I didn't even know to ask. Elevate helped me understand my options without a hard sell.", pic: "https://images.pexels.com/photos/27086922/pexels-photo-27086922.jpeg?w=200" },
  { name: "David Chen", role: "Head of Finance, Halcyon", quote: "Their institutional partner network is legitimate. Straightforward and honest through the entire conversation.", pic: "https://images.pexels.com/photos/37148308/pexels-photo-37148308.jpeg?w=200" },
];

const faqs = [
  { q: "Is Elevate Financial a bank?", a: "No. Elevate Financial is not a bank and is not FDIC-insured. We are a lending-information and referral service that connects you with third-party lending partners for consideration." },
  { q: "Will you ask for my Social Security number on this website?", a: "No. This website does not collect your Social Security number, bank account numbers, or account passwords. If a lending partner needs additional information, they will collect it securely through their own regulated intake process — not this site." },
  { q: "Is submitting a request a credit application?", a: "No. A request for information on this site is not a credit application or credit offer. If you decide to move forward with a partner, they will conduct their own application and disclosures under their own terms." },
  { q: "Will you send my information to third parties?", a: "We may share the information you submit with lending partners who can help match you with credit products, only for the purpose of responding to your inquiry. See our Privacy Notice for details." },
  { q: "How do I opt out of contact?", a: "You can opt out at any time by replying STOP to any SMS message, clicking unsubscribe in emails, or asking a specialist to remove your record. Consent to contact is not a condition of any product or service." },
  { q: "How is my information protected?", a: "We use industry-standard encryption in transit and at rest, and we only collect information required to respond to your request." },
];

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General inquiry", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "General inquiry", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input required data-testid="contact-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 h-11 text-white placeholder:text-white/40" />
        <Input required type="email" data-testid="contact-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/10 h-11 text-white placeholder:text-white/40" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input data-testid="contact-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white/5 border-white/10 h-11 text-white placeholder:text-white/40" />
        <Input required data-testid="contact-subject" placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-white/5 border-white/10 h-11 text-white placeholder:text-white/40" />
      </div>
      <Textarea required data-testid="contact-message" placeholder="How can we help?" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-white/40" />
      <Button type="submit" disabled={busy} data-testid="contact-submit" className="ef-btn-primary rounded-full px-7 h-11">
        {busy ? "Sending..." : <>Send message <Send className="h-4 w-4 ml-2" /></>}
      </Button>
    </form>
  );
}

export default function Home() {
  return (
    <div className="text-white">
      {/* HERO */}
      <section id="request-info" className="relative overflow-hidden pt-10 md:pt-16 pb-24 md:pb-32 ef-hero-radial ef-noise">
        <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/3137050/pexels-photo-3137050.jpeg')] bg-cover bg-center opacity-[0.08]" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="ef-overline mb-6">
              <span className="ef-gold-text">Elevate Financial · Transparent Lending Information</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }} className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[0.98] tracking-[-0.02em]">
              Fast, Secure & Affordable <br className="hidden sm:block" />
              <span className="italic ef-gold-text">Personal Loans.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mt-6 text-base md:text-lg text-white/70 leading-relaxed max-w-xl">
              Apply today and get a decision quickly. Elevate Financial connects you with a network of licensed lending partners — with clear terms, transparent rates, and no gotchas.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-8 flex flex-wrap gap-3">
              <Link to="/apply"><Button data-testid="hero-apply-now" className="ef-btn-primary rounded-full px-7 h-12 text-sm font-medium">Apply Now <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
              <a href="#loan-calculator"><Button data-testid="hero-calculator" variant="outline" className="ef-btn-ghost rounded-full px-6 h-12 text-sm font-medium text-white bg-transparent">Estimate my payment</Button></a>
            </motion.div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs font-mono uppercase tracking-widest text-white/50">
              <div className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-[#17A673]" /> Transparent Rates</div>
              <div className="flex items-center gap-2"><Lock className="h-3.5 w-3.5 text-[#D4AF37]" /> Encrypted</div>
              <div className="flex items-center gap-2"><Award className="h-3.5 w-3.5 text-[#1B63F0]" /> No Hidden Fees</div>
            </div>
          </div>

          {/* Lead capture form (compliant lead intake — no PII) */}
          <div id="request-info-form" className="lg:col-span-5 relative">
            <LeadForm />
          </div>
        </div>
      </section>

      {/* LOAN CALCULATOR */}
      <section id="loan-calculator" className="py-24 bg-[#050f22]">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl mb-10">
            <div className="ef-overline">Loan Calculator</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mt-4">See what your <span className="italic ef-gold-text">payment</span> looks like.</h2>
            <p className="mt-4 text-white/60">Adjust the amount, APR, and term to estimate your monthly payment. Illustrative only.</p>
          </div>
          <LoanCalculator />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <HowItWorks />

      {/* PRODUCTS */}
      <section id="products" className="py-24 bg-[#050f22]">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <div className="ef-overline">Loan Products</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mt-4">Options across the <em className="ef-gold-text not-italic">categories that matter.</em></h2>
            <p className="mt-4 text-white/60">Detailed information on every product our partners offer — with example rates and eligibility.</p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {loanProducts.map((p) => {
              const Icon = p.icon;
              return (
                <Link key={p.slug} to={`/loans/${p.slug}`} data-testid={`product-card-${p.slug}`}>
                  <motion.div whileHover={{ y: -6 }} className="ef-card p-7 h-full flex flex-col">
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-5" style={{ background: `${p.accent}18`, border: `1px solid ${p.accent}40` }}>
                      <Icon className="h-5 w-5" style={{ color: p.accent }} />
                    </div>
                    <div className="font-display text-xl font-bold">{p.title}</div>
                    <p className="mt-1.5 text-sm text-white/60">{p.desc}</p>
                    <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]">{p.range}</div>
                    <span className="mt-auto pt-5 inline-flex items-center text-sm font-medium text-[#D4AF37] group-hover:text-white">
                      View details <ArrowRight className="h-4 w-4 ml-1.5" />
                    </span>
                  </motion.div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <div className="ef-overline">Why Elevate</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mt-4">Built for people who read the <span className="italic ef-gold-text">fine print.</span></h2>
          </div>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-5">
            {whyChoose.map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} className="ef-card p-6">
                  <Icon className="h-6 w-6 text-[#D4AF37]" />
                  <div className="font-display text-lg font-semibold mt-4">{w.title}</div>
                  <div className="text-sm text-white/60 mt-2 leading-relaxed">{w.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <Partners />

      {/* ABOUT */}
      <section id="about" className="py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="ef-overline">About Elevate</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mt-4">A brand named for <span className="italic ef-gold-text">why</span> — not what.</h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-white/75 leading-relaxed">
            <p>Elevate Financial helps people turn intention into real financial progress. We offer clear, transparent information about lending options and refer interested consumers to trusted third-party partners.</p>
            <p>No gotchas. No traps. Just information that's easy to understand and specialists who are willing to answer your questions.</p>
            <p className="text-white/60">Elevate Financial is not a bank. We do not accept deposits, hold customer funds, or issue credit ourselves.</p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-[#050f22]">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="ef-overline">People We've Helped</div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mt-4 max-w-3xl">Real progress, spoken plainly.</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="ef-card p-7 flex flex-col" data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}>
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="h-3.5 w-3.5 fill-[#D4AF37] text-[#D4AF37]" />)}
                </div>
                <p className="text-white/85 leading-relaxed">"{t.quote}"</p>
                <div className="flex items-center gap-3 mt-6 pt-6 border-t border-white/10">
                  <img src={t.pic} alt={t.name} className="h-11 w-11 rounded-full object-cover" />
                  <div>
                    <div className="text-sm font-semibold">{t.name}</div>
                    <div className="text-xs text-white/50">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="ef-overline">FAQ</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mt-4">Questions, <span className="italic ef-gold-text">answered.</span></h2>
            <p className="mt-4 text-white/60">Straightforward answers about what we do and how your information is handled.</p>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                  <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left font-display text-lg hover:no-underline hover:text-[#D4AF37]">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-white/70 text-sm leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* SECURITY & PRIVACY */}
      <section id="security" className="py-24 bg-[#050f22]">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <div className="ef-overline">Security & Privacy</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mt-4">Your data, <span className="italic ef-gold-text">handled with care.</span></h2>
            <p className="mt-4 text-white/60">Modern encryption in transit and at rest. We collect the minimum necessary and never sell your personal information.</p>
          </div>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: Lock, title: "SSL Secure" },
              { icon: ShieldCheck, title: "256-bit Encryption" },
              { icon: Fingerprint, title: "Fraud Protection" },
              { icon: KeyRound, title: "Secure Login" },
              { icon: FileCheck2, title: "Data Encryption" },
              { icon: HeartPulse, title: "24/7 Monitoring" },
            ].map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="ef-card p-5 text-center" data-testid={`security-badge-${b.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="mx-auto h-10 w-10 rounded-xl bg-[#17A673]/15 border border-[#17A673]/40 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-[#17A673]" />
                  </div>
                  <div className="mt-3 text-xs font-mono uppercase tracking-widest text-white/70">{b.title}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATE LICENSING & COMPLIANCE */}
      <section id="licensing" className="py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="ef-overline">State Licensing & Compliance</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mt-4">Compliance is not an <span className="italic ef-gold-text">afterthought.</span></h2>
            <p className="mt-4 text-white/60">
              Elevate Financial is a lead-generation service that connects consumers with licensed U.S. lending partners. All loans are originated by our partners under their own state and federal licenses.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { title: "Equal Credit Opportunity Act (ECOA)", desc: "We prohibit discrimination in credit based on race, color, religion, national origin, sex, marital status, age, or any other protected class." },
              { title: "Fair Credit Reporting Act (FCRA)", desc: "Consumer credit information is handled in accordance with the FCRA and its implementing regulations." },
              { title: "Truth in Lending Act (TILA)", desc: "Our partner lenders provide TILA disclosures for any credit offered, including APR, finance charges, and payment schedule." },
              { title: "Gramm-Leach-Bliley Act (GLBA)", desc: "We maintain administrative, technical, and physical safeguards to protect nonpublic personal information." },
              { title: "E-SIGN Act", desc: "Electronic signatures and disclosures are used with your consent per the E-SIGN Act." },
              { title: "USA PATRIOT Act", desc: "Our partners perform customer identification per federal law prior to funding any loan." },
            ].map((r) => (
              <div key={r.title} className="ef-card p-6" data-testid={`compliance-${r.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]">Regulation</div>
                <div className="font-display text-base font-bold mt-2">{r.title}</div>
                <p className="mt-2 text-xs text-white/60 leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 bg-[#050f22]">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="ef-overline">Contact</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mt-4">Talk to a real person.</h2>
            <p className="mt-4 text-white/60 max-w-md">Our team is available Monday through Friday, 8am–8pm ET, and Saturdays 9am–5pm ET.</p>
            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-[#1B63F0]/15 border border-[#1B63F0]/40 flex items-center justify-center"><MapPin className="h-4 w-4 text-[#1B63F0]" /></div>
                <div>
                  <div className="text-sm font-semibold">Elevate Financial</div>
                  <div className="text-sm text-white/60">Contact us by phone or email — see below.</div>
                </div>
              </div>
              <a href="tel:+17865989004" data-testid="specialist-phone-link" className="flex items-start gap-4 group">
                <div className="h-10 w-10 rounded-full bg-[#17A673]/15 border border-[#17A673]/40 flex items-center justify-center group-hover:bg-[#17A673]/25 transition-colors"><Phone className="h-4 w-4 text-[#17A673]" /></div>
                <div>
                  <div className="text-sm font-semibold group-hover:text-[#17A673] transition-colors">+1 (786) 598 9004</div>
                  <div className="text-sm text-white/60">Mon–Fri 8am–8pm · Sat 9am–5pm ET</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center"><Mail className="h-4 w-4 text-[#D4AF37]" /></div>
                <div>
                  <div className="text-sm font-semibold"><a href="mailto:hello@elevatefinancialloans.com" className="hover:text-[#D4AF37]">hello@elevatefinancialloans.com</a></div>
                  <div className="text-sm text-white/60"><a href="mailto:support@elevatefinancialloans.com" className="hover:text-[#D4AF37]">support@elevatefinancialloans.com</a></div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="ef-card p-8">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
